import React, { useState, useRef, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import Modal from 'components/features/modal/Modal';
import Icon from 'components/commons/icon/Icon';
import Pagination, { PageParamType } from 'components/features/Pagination';
import { useRouting } from 'hooks/useRouting';
import { useScroll } from 'hooks/useScroll';
import { formatDateForString } from 'utils/formatter';
import {
  getNotificationSiteTypeStorage,
  updateNotificationSiteType,
  AssetFilterItemsProps,
} from 'utils/storage';

import * as consts from 'config/consts';
import * as api from 'config/api';

import * as env from 'config/environment';

//------------------------------------------------------------
// タブメニュー種別
//------------------------------------------------------------
// memo: GetNotifiationAPIから取得したtypeの採番とは関連なし
export const menuTypes = {
  サイトから: '1',
  資産関連: '2',
  重要な情報: '3',
  適時開示: '4',
};

//------------------------------------------------------------
// お知らせ設定フィルタに該当するデータを抽出
//------------------------------------------------------------
export const notificationSettingFilter = (
  ret: api.GetNotificationResult,
  menuType: string
): api.GetNotificationResult => {
  const filterItems = ret.Items.filter(item => {
    switch (menuType) {
      case menuTypes.サイトから:
        return item.Type === consts.notificationTypes.通常お知らせ;
      case menuTypes.資産関連:
        return (
          item.Type === consts.notificationTypes.レポート ||
          item.Type === consts.notificationTypes.経済指標 ||
          item.Type === consts.notificationTypes.資産形成ガイド
        );
      case menuTypes.適時開示:
        return item.Type === consts.notificationTypes.適時開示;
      case menuTypes.重要な情報:
        return item.Type === consts.notificationTypes.重要なお知らせ;
      default:
        return false;
    }
  });
  return { Items: filterItems };
};

//------------------------------------------------------------
// カテゴリ名称
//------------------------------------------------------------
export const categoryNames = [
  '重要なお知らせ',
  'お知らせ',
  '',
  '適時開示',
  'レポート',
  '経済指標',
  '資産形成ガイド',
];

//------------------------------------------------------------
// 文字列にダブルクォーテーションを付与
//------------------------------------------------------------
export function setQuote(value: string): string {
  return `"${value}"`;
}

// function initPageNo(pageNo: any): number {
//   return Number.isInteger(pageNo) && pageNo > 0 ? Number(pageNo) : 1;
// }

// お知らせ一覧の１ページあたりの最大表示件数
const maxRecordsPerPage = 10;

//------------------------------------------------------------
// お知らせ一覧
//------------------------------------------------------------
const NotificationList: React.FC = () => {
  // URLパラメータ情報
  const { menuNo, startPage, selectPage } = useParams<{
    // タブメニュー選択
    menuNo: string;
    // ページネーションの開始ページ
    startPage: string;
    // ページネーションの選択ページ
    selectPage: string;
  }>();

  const routing = useRouting();

  // getNotificationsAPIのレスポンス全量
  const [notificationResult, setNotificationResult] = useState<
    api.GetNotificationResult | undefined
  >(undefined);
  // 選択しているtypeのお知らせ情報全量
  const [notifications, setNotifications] = useState<
    api.GetNotificationResult | undefined
  >(undefined);
  // 表示対象のお知らせ
  const [notificationItems, setNotificationItems] = useState<
    api.GetNotificationItem[]
  >([]);
  // 既読一括ダイアログ表示
  const [allReadModalShow, setAllReadModalShow] = useState<boolean>(false);
  // historyオブジェクト
  const history = useHistory();
  const unmounted = useRef<boolean>(false);
  // タブメニュー選択
  const [menuType, setMenuType] = useState<string>(menuNo);

  const initialInputFilter = {
    report: false,
    economicIndicators: false,
    assetFormationGuide: false,
  } as AssetFilterItemsProps;
  const notificationSiteType = getNotificationSiteTypeStorage();
  const inputFilter_ =
    notificationSiteType !== null ? notificationSiteType : initialInputFilter;
  // 資産関連タブのフィルター
  const [inputFilter, setInputFilter] = useState<AssetFilterItemsProps>(
    inputFilter_
  );

  const isMenuTypeAssetSelected = menuType === menuTypes.資産関連;

  const [siteAllReadStatus, setSiteAllReadStatus] = useState<boolean>(false);
  const [assetsAllReadStatus, setAssetsAllReadStatus] = useState<boolean>(
    false
  );
  const [
    timelyDisclosureAllReadStatus,
    setTimelyDisclosureAllReadStatus,
  ] = useState<boolean>(false);
  const importNotificationAllReadStatus = true;

  //------------------------------------------------------------
  // ページネーション
  //------------------------------------------------------------
  // 総ページ数
  const [totalPageCount, setTotalPageCount] = useState<number | undefined>(
    undefined
  );

  //------------------------------------------------------------
  // 既読一括ダイアログの表示/非表示
  //------------------------------------------------------------
  const AllReadModalShow = (): void => {
    setAllReadModalShow(true);
  };
  const AllReadModalClose = (): void => {
    setAllReadModalShow(false);
  };

  //------------------------------------------------------------
  // 既読判定
  //------------------------------------------------------------
  const isReadStatus = (ReadStatus: boolean | null): boolean => {
    return ReadStatus === true;
  };

  //------------------------------------------------------------
  // 既読一括処理
  //------------------------------------------------------------
  const AllRead = () => {
    // 表示されている未読のIDを抽出
    const notificationIDs: string[] = notificationItems
      .filter(item => {
        return !isReadStatus(item.ReadStatus);
      })
      .map(item => {
        return item.ID;
      });

    // 既読更新を呼び出す
    const IDList: string[] = notificationIDs.map(ID => {
      return setQuote(ID);
    });
    api.setReadNotification(IDList).then(ret => {
      // 既読更新が成功
      if (ret.Success) {
        // 未読を既読状態に変更
        const newNotificationItem = notificationItems.map(item => {
          if (!isReadStatus(item.ReadStatus)) {
            item.ReadStatus = true;
          }
          return item;
        });
        // 表示明細の既読状態に更新
        setNotificationItems(newNotificationItem);
      }
    });

    // 既読一括ダイアログをクローズ
    AllReadModalClose();
  };

  //------------------------------------------------------------
  // お知らせ設定パネル
  //------------------------------------------------------------
  // Ph2.0はMask
  // const gotoNotificationSetting = (): void => {
  //   history.push(`/notificationSetting`);
  // };

  //------------------------------------------------------------
  // お知らせ一覧（ページ遷移）
  //------------------------------------------------------------
  const gotoNotificationList = (
    menuNo: string,
    startPage: string,
    selectPage: string
  ): void => {
    history.push(`/notificationList/${menuNo}/${startPage}/${selectPage}`);
  };

  //------------------------------------------------------------
  // お知らせ詳細
  //------------------------------------------------------------
  const gotoNotificationDetail = (
    menuNo: string,
    notificationID: string
  ): void => {
    routing.pushRouting(
      { path: `/notificationDetail/${menuNo}/${notificationID}` },
      true,
      window.scrollY
    );
  };

  //------------------------------------------------------------
  // お知らせ設定
  //------------------------------------------------------------
  const goToNotificationSetting = (): void => {
    routing.pushRouting({ path: '/notificationSetting' }, true, window.scrollY);
  };

  //------------------------------------------------------------
  // お知らせ一覧（スタイル・高さの自動計算）
  //------------------------------------------------------------
  const getNotificationItemsStyle = () => {
    const sh = window.innerHeight;
    const hh = document.getElementById('id_header')?.offsetHeight;
    const fh = document.getElementById('id_footer')?.offsetHeight;
    const lh = document.getElementById('id_lineBanner')?.offsetHeight;
    const vhh = document.getElementById('id_notificationViewHeader')
      ?.offsetHeight;
    const vmh = document.getElementById('id_notificationViewMenu')
      ?.offsetHeight;
    const vph = document.getElementById('id_notificationViewPagination')
      ?.offsetHeight;

    if (!sh || !hh || !fh || !lh || !vhh || !vmh || !vph) return {};
    return { height: sh - (hh + fh + lh + vhh + vmh + vph) + 'px' };
  };

  //------------------------------------------------------------
  // ページ変更
  //------------------------------------------------------------
  const onChangePage = (newPageParam: PageParamType): void => {
    gotoNotificationList(
      menuType,
      newPageParam.startPage.toString(),
      newPageParam.selectPage.toString()
    );
  };

  //------------------------------------------------------------
  // 指定ページの該当データを抽出
  //------------------------------------------------------------
  const paginationFilter = (
    items: api.GetNotificationItem[],
    selectPage: number
  ): api.GetNotificationItem[] => {
    const max = items.length;
    return items.filter((item, index) => {
      const nSelectPage = Number(selectPage);
      let stIndex: number = (nSelectPage - 1) * maxRecordsPerPage;
      let edIndex: number = nSelectPage * maxRecordsPerPage - 1;

      if (stIndex > max) stIndex = max;
      if (edIndex > max) edIndex = max;

      return stIndex <= index && edIndex >= index;
    });
  };

  //------------------------------------------------------------
  // 「資産関連」選択時のお知らせタイプフィルター処理
  //------------------------------------------------------------
  const assetFilterItems = [
    {
      text: 'レポート',
      name: 'report',
      checked: inputFilter.report,
    },
    {
      text: '経済指標',
      name: 'economicIndicators',
      checked: inputFilter.economicIndicators,
    },
    {
      text: '資産形成ガイド',
      name: 'assetFormationGuide',
      checked: inputFilter.assetFormationGuide,
    },
  ];

  const onChangeAssetFilterItem = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const updFilter = {
      ...inputFilter,
      [event.target.dataset.name as string]: event.target.checked,
    };
    updateNotificationSiteType(
      updFilter.report,
      updFilter.economicIndicators,
      updFilter.assetFormationGuide
    );
    setInputFilter(updFilter);

    gotoNotificationList('2', '1', '1');
  };

  const getFilteredAssetData = (
    data: api.GetNotificationResult
  ): api.GetNotificationResult => {
    const getSelectedTypes = () => {
      const list = Object.entries(inputFilter);
      if (list.every(item => item[1] === false)) {
        return [
          consts.notificationTypes.レポート,
          consts.notificationTypes.経済指標,
          consts.notificationTypes.資産形成ガイド,
        ];
      } else {
        return list
          .map(([key, value]) => {
            if (!value) return null;
            switch (key) {
              case 'report':
                return consts.notificationTypes.レポート;
              case 'economicIndicators':
                return consts.notificationTypes.経済指標;
              case 'assetFormationGuide':
                return consts.notificationTypes.資産形成ガイド;
            }
          })
          .filter(x => x) as number[];
      }
    };
    const selectedTypes = getSelectedTypes();

    const filteredData = data.Items.filter(item =>
      selectedTypes.includes(item.Type)
    );

    return { Items: filteredData } as api.GetNotificationResult;
  };

  // マウント時
  useEffect(() => {
    return () => {
      // unmount
      unmounted.current = true;
    };
  }, []);

  useEffect(() => {
    if (unmounted.current) return;

    // お知らせ一覧の取得
    api.getNotifications().then(ret_ => {
      if (!unmounted.current && ret_) {
        const filteredItems = ret_.Items.filter(
          item => item.BeforeLogin !== consts.NotificationCode.投稿
        );
        const ret = { Items: filteredItems } as api.GetNotificationResult;
        setNotificationResult(ret);

        // お知らせ設定の内容で絞り込む
        const dataFilter = notificationSettingFilter(ret, menuType);
        const targetData = isMenuTypeAssetSelected
          ? getFilteredAssetData(dataFilter)
          : dataFilter;

        // 対象のお知らせ一覧の件数を保持
        setTotalPageCount(
          Math.ceil(targetData.Items.length / maxRecordsPerPage)
        );
        // 対象のお知らせ一覧を保持
        setNotifications(targetData);

        const siteNotifications = notificationSettingFilter(
          ret,
          menuTypes.サイトから
        );
        const asstsNotifications = notificationSettingFilter(
          ret,
          menuTypes.資産関連
        );
        const timelyDisclosureNotifications = notificationSettingFilter(
          ret,
          menuTypes.適時開示
        );

        setSiteAllReadStatus(
          siteNotifications.Items.every(item => item.ReadStatus === true)
        );
        setAssetsAllReadStatus(
          asstsNotifications.Items.every(item => item.ReadStatus === true)
        );
        setTimelyDisclosureAllReadStatus(
          timelyDisclosureNotifications.Items.every(
            item => item.ReadStatus === true
          )
        );
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuType]);

  useEffect(() => {
    if (notificationResult === undefined || !isMenuTypeAssetSelected) return;
    const dataFilter = notificationSettingFilter(notificationResult, menuType);
    const targetData = getFilteredAssetData(dataFilter);

    setNotifications(targetData);
    setTotalPageCount(Math.ceil(targetData.Items.length / maxRecordsPerPage));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputFilter]);

  // ページ変更
  useEffect(() => {
    if (!unmounted.current && notifications) {
      // 対象ページの情報を絞り込む
      const newItems = paginationFilter(
        notifications.Items,
        Number(selectPage)
      );
      setNotificationItems(newItems);
    }
  }, [notifications, selectPage]);

  // サブメニュー
  const subMenu = [
    {
      type: menuTypes.サイトから,
      text: 'サイトから',
    },
    {
      type: menuTypes.資産関連,
      text: '資産関連',
    },
    {
      type: menuTypes.適時開示,
      text: '適時開示',
    },
    {
      type: menuTypes.重要な情報,
      text: '重要な情報',
    },
  ].map((item, index) => {
    const div_base =
      'flex-1 text-center bg-white py-2 px-1 text-xs text-gray-500 border-t border-gray-300';

    const div_side =
      item.type === menuTypes.サイトから
        ? 'border-l rounded-tl'
        : item.type === menuTypes.重要な情報
        ? 'border-r rounded-tr'
        : '';

    const div_active =
      item.type === menuType ? 'ui-tab-menu-active' : 'cursor-pointer';

    const isAllRead =
      item.type === menuTypes.サイトから
        ? siteAllReadStatus
        : item.type === menuTypes.資産関連
        ? assetsAllReadStatus
        : item.type === menuTypes.適時開示
        ? timelyDisclosureAllReadStatus
        : importNotificationAllReadStatus;

    return (
      <div
        key={index}
        className={`flex flex-row justify-center ${div_base} ${div_side} ${div_active}`}
        onClick={(): void => {
          setMenuType(item.type);
          gotoNotificationList(item.type, '1', '1');
        }}>
        <div className="flex items-center">{item.text}</div>
        <div
          className={`ml-1 h-2 w-2 rounded-full bg-tradewell ${
            isAllRead ? 'hidden' : ''
          }`}></div>
      </div>
    );
  });

  const getBorderClass = (index: number, length: number) => {
    return index === 0
      ? 'border-t border-l border-r'
      : index === length - 1
      ? 'border rounded-b'
      : 'border-t border-l border-r';
  };
  const goTimelyDisclosure = () =>
    routing.pushRouting({ path: '/timelyDisclosure' }, true);
  // お知らせ一覧ビュー
  const notificationItemsView =
    notificationItems.length === 0 ? (
      <div
        className={`border-gray-300 border-solid mx-2 bg-white text-center ${getBorderClass(
          1,
          1
        )}`}
        style={{
          padding: '5rem 0 5rem',
          display: 'grid',
          placeContent: 'center',
        }}>
        {menuType !== menuTypes.適時開示 ? (
          <>お知らせがありません</>
        ) : (
          <>
            <div>設定されていません</div>
            <div
              className="text-blue-700 cursor-pointer"
              onClick={(): void => goTimelyDisclosure()}>
              適時開示の設定へ
            </div>
          </>
        )}
      </div>
    ) : (
      notificationItems.map((item, index, array) => {
        return (
          <div
            className={`border-gray-300 border-solid mx-2 p-2 ${getBorderClass(
              index,
              array.length
            )} bg-white cursor-pointer`}
            key={index}
            onClick={() => gotoNotificationDetail(menuType, item.ID)}>
            <div className="flex text-sm m-1">
              <div className="w-8" />
              <div className="flex">
                <div className="">
                  {formatDateForString(item.InsTime, 'yyyy/MM/dd HH:mm')}
                </div>
                <div className="ml-4">{categoryNames[item.Type]}</div>
              </div>
            </div>
            <div className="flex m-1">
              <div className="w-8">
                <div
                  className={`mt-1 ml-2 h-3 w-3 rounded-full bg-tradewell ${isReadStatus(
                    item.ReadStatus ||
                      item.Type === consts.notificationTypes.重要なお知らせ
                  ) && 'hidden'}`}>
                  {item.ReadStatus}
                </div>
              </div>
              <div
                className={`w-full whitespace-pre-wrap break-all ${!isReadStatus(
                  item.ReadStatus
                ) &&
                  !(item.Type === consts.notificationTypes.重要なお知らせ) &&
                  'font-bold'}`}>
                {item.Title}
              </div>
            </div>
          </div>
        );
      })
    );

  useScroll();

  return (
    <div>
      {/* 既読一括ダイアログ */}
      {allReadModalShow && (
        <div onClick={AllReadModalClose}>
          <Modal onClose={AllReadModalClose}>
            <div
              onClick={event => {
                event.stopPropagation();
              }}>
              <div className="flex pt-3 px-3 w-screen-90">
                <div className="flex-grow"></div>
                <button className="" onClick={AllReadModalClose}>
                  <Icon
                    name="close"
                    className="text-2xl text-gray-300 text-center"
                  />
                </button>
              </div>
              <div className="text-center">
                <div>すべてのお知らせを既読にします。</div>
                <div>よろしいですか？</div>
              </div>
              <div className="items-center mx-5 my-4">
                <div className="ui-ok-btn cursor-pointer" onClick={AllRead}>
                  OK
                </div>
                <div
                  className="ui-cancel-btn cursor-pointer"
                  onClick={AllReadModalClose}>
                  キャンセル
                </div>
              </div>
            </div>
          </Modal>
        </div>
      )}

      {/* お知らせ一覧パネル */}
      {notifications && (
        <div>
          <div className="w-full" id="id_lineBanner">
            <a id="notification_line" href={env.lineAddFriendURL}>
              <img
                src="/static/images/line1.png"
                alt="友だち追加"
                className="mx-auto my-2 px-8"
              />
            </a>
          </div>
          {/*  メニュー */}
          <div className="flex text-link" id="id_notificationViewHeader">
            <div
              className="flex items-center mt-3 mb-2 ml-3 cursor-pointer"
              onClick={AllReadModalShow}>
              <Icon name="check" className=""></Icon>
              <div className="ml-1">全て既読にする</div>
            </div>
            <div
              className="flex flex-row items-center mt-3 mb-2 mr-3 ml-auto cursor-pointer"
              onClick={goToNotificationSetting}>
              <Icon name="mobile_friendly" className=""></Icon>
              <div className="ml-1">お知らせ設定</div>
            </div>
            {/* Ph2.0はMask */}
            {/* <div className="mt-3 mb-2 mr-3" onClick={gotoNotificationSetting}>お知らせ設定</div> */}
          </div>

          {/* サブメニュー */}
          <div className="container" id="id_notificationViewMenu">
            <div className="flex mx-2">{subMenu}</div>
          </div>

          {isMenuTypeAssetSelected ? (
            <div className="flex flex-row items-center pt-2 px-2 mx-2 bg-white border-t border-l border-r border-gray-300">
              {assetFilterItems.map((item, index) => {
                return (
                  <label
                    className="ui-checkbox-button-sm theme-simple flex flex-row items-center ml-2 rounded cursor-pointer"
                    htmlFor={`ofm_chkProduct_${index}`}
                    key={index}>
                    <input
                      id={`ofm_chkProduct_${index}`}
                      type="checkbox"
                      checked={item.checked}
                      data-name={item.name}
                      onChange={onChangeAssetFilterItem}
                    />
                    <div className="ui-checkbox-button-text bg-bluegray-50">
                      <div className="text-xs text-gray-800">{item.text}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          ) : (
            <></>
          )}

          {/* お知らせ一覧 */}
          <div
            id="id_notificationItemsView"
            className="overflow-y-auto"
            style={getNotificationItemsStyle()}>
            {notificationItemsView}
          </div>

          {/* ページネーション */}
          <div id="id_notificationViewPagination">
            <Pagination
              startPage={Number(startPage)}
              selectPage={Number(selectPage)}
              totalPageCount={totalPageCount}
              onChangePage={onChangePage}
            />
          </div>

          <div
            className="grid border-gray-300 border-solid mt-4 mb-8 mx-2 bg-white rounded"
            style={{ gridTemplateColumns: '1fr auto' }}>
            <div
              className="flex flex-row items-center cursor-pointer"
              onClick={goToNotificationSetting}>
              <div className="ml-2">
                <img
                  src={`/static/images/notification-setting.svg`}
                  alt={`お知らせ設定`}
                  className={`mt-2`}
                />
              </div>
              <div className="ml-3 text-xl font-bold">お知らせ設定</div>
            </div>
            <div
              className={`px-1 justify-center items-center flex flex-row bg-bluegray-50 border-l border-bluegray-100 rounded-r-lg`}>
              <div className="icon-chevron-right text-bluegray-400 font-bold flex flex-row"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationList;
